import * as React from 'react';
import { Activity } from 'lucide-react';
import { usePermission } from '@tyndo/hooks';
import { WidgetCard } from '../widget-card';
import { WidgetStateView } from '../widget-card';
import { useWidgetData } from '../widget-contract';
import type { WidgetDescriptor, WidgetSize } from '../widget-contract';
import {
  toneTextClass,
  type StatusModel,
  type StatusMetric,
  type WidgetPermissionRef,
} from './models';

/**
 * `CanonicalStatusWidget` — the reusable **status** kind.
 *
 * Renders a module health / summary readout: an optional emphasized headline
 * metric plus a KPI grid. The caller supplies the descriptor (which carries the
 * REAL endpoint + refresh cadence + drill-through), the permission to gate on,
 * and a `select` projector mapping the real API response to a `StatusModel`.
 *
 * All loading / error / empty / permission / stale states come from
 * `useWidgetData` over the real query — nothing is fabricated.
 */
export interface CanonicalStatusWidgetProps<TRaw> {
  descriptor: WidgetDescriptor;
  /** Resource+action fed to `usePermission`. */
  permission: WidgetPermissionRef;
  /** Project the REAL API response into the canonical status view-model. */
  select: (raw: TRaw) => StatusModel;
  /** Optional empty override; defaults to the framework's null/[]/{} check. */
  isEmpty?: (raw: TRaw | null | undefined) => boolean;
  widgetSize?: WidgetSize;
  icon?: React.ReactNode;
  emptyTitle?: string;
  emptyDescription?: string;
}

function MetricCell({ metric }: { metric: StatusMetric }): React.ReactElement {
  return (
    <div className="flex flex-col" data-testid={`status-metric-${metric.key}`}>
      <span className={`text-lg font-semibold tabular-nums ${toneTextClass(metric.tone)}`}>
        {metric.value}
      </span>
      <span className="text-[11px] text-muted-foreground">{metric.label}</span>
      {metric.hint ? (
        <span className="text-[10px] text-muted-foreground/80">{metric.hint}</span>
      ) : null}
    </div>
  );
}

export function CanonicalStatusWidget<TRaw>({
  descriptor,
  permission,
  select,
  isEmpty,
  widgetSize = 'small',
  icon,
  emptyTitle = 'No status yet',
  emptyDescription = 'There is no summary to show for this module yet.',
}: CanonicalStatusWidgetProps<TRaw>): React.ReactElement {
  const perm = usePermission(permission.resource, permission.action);

  const state = useWidgetData<TRaw, StatusModel>({
    widgetId: descriptor.widgetId,
    dataSource: descriptor.dataSource,
    cachePolicy: 'memory',
    permissionDenied: !perm.loading && !perm.allowed,
    project: (raw) => select(raw as TRaw),
    isEmpty: isEmpty as ((d: TRaw | null | undefined) => boolean) | undefined,
  });

  const maxMetrics = widgetSize === 'small' ? 4 : 8;

  return (
    <WidgetCard
      descriptor={descriptor}
      source={state.status === 'ready' ? state.source : undefined}
      onRefresh={state.refetch}
      icon={icon ?? <Activity className="h-4 w-4" aria-hidden />}
    >
      <WidgetStateView
        state={state}
        emptyTitle={emptyTitle}
        emptyDescription={emptyDescription}
        onRetry={state.refetch}
      >
        {(data) => (
          <div className="flex h-full flex-col gap-3" data-testid="status-widget-body">
            {data.headline ? (
              <div className="flex items-baseline gap-2" data-testid="status-widget-headline">
                <span
                  className={`text-3xl font-bold tabular-nums ${toneTextClass(data.headline.tone)}`}
                >
                  {data.headline.value}
                </span>
                <span className="text-xs text-muted-foreground">{data.headline.label}</span>
              </div>
            ) : null}
            {data.metrics.length > 0 ? (
              <div className="grid grid-cols-2 gap-x-4 gap-y-2" data-testid="status-widget-grid">
                {data.metrics.slice(0, maxMetrics).map((m) => (
                  <MetricCell key={m.key} metric={m} />
                ))}
              </div>
            ) : null}
          </div>
        )}
      </WidgetStateView>
    </WidgetCard>
  );
}

export default CanonicalStatusWidget;
